import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getBookingRules, updateBookingRules, listGuestFlags, toggleGuestFlag } from "@/lib/admin.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/admin/booking-control")({
  component: BookingControl,
});

function BookingControl() {
  const getFn = useServerFn(getBookingRules);
  const saveFn = useServerFn(updateBookingRules);
  const flagsFn = useServerFn(listGuestFlags);
  const toggleFn = useServerFn(toggleGuestFlag);
  const qc = useQueryClient();
  const { data: rules } = useQuery({ queryKey: ["booking-rules"], queryFn: () => getFn() });
  const { data: flags = [] } = useQuery({ queryKey: ["guest-flags"], queryFn: () => flagsFn() });

  const [form, setForm] = useState<{
    id: number; auto_confirm_max_party: number; group_min_party: number;
    group_deposit_per_guest_cents: number; pool_deposit_cents: number; late_cutoff_hour: number;
  } | null>(null);

  useEffect(() => { if (rules && !form) setForm(rules as never); }, [rules, form]);

  const save = useMutation({
    mutationFn: async () => { if (form) await saveFn({ data: form }); },
    onSuccess: () => { toast.success("Rules updated"); qc.invalidateQueries({ queryKey: ["booking-rules"] }); },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => toggleFn({ data: { id, active } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["guest-flags"] }),
  });

  return (
    <div className="space-y-10">
      <div>
        <h1 className="font-display text-3xl">Booking control</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Auto-confirm rules, deposits and active guest flags.
        </p>
      </div>

      {form && (
        <form
          onSubmit={(e) => { e.preventDefault(); save.mutate(); }}
          className="grid gap-4 rounded-2xl border border-border bg-card/40 p-5 md:grid-cols-2"
        >
          <div><Label>Auto-confirm up to (party size)</Label><Input type="number" value={form.auto_confirm_max_party} readOnly /></div>
          <div><Label>Email-only from (party size)</Label><Input type="number" value={form.group_min_party} readOnly /></div>
          <div><Label>Group deposit / guest (€ cents)</Label><Input type="number" min={0} value={form.group_deposit_per_guest_cents} onChange={(e) => setForm({ ...form, group_deposit_per_guest_cents: +e.target.value })} /></div>
          <div><Label>Pool deposit (€ cents)</Label><Input type="number" min={0} value={form.pool_deposit_cents} onChange={(e) => setForm({ ...form, pool_deposit_cents: +e.target.value })} /></div>
          <div><Label>Late cutoff hour (0–23)</Label><Input type="number" min={0} max={23} value={form.late_cutoff_hour} onChange={(e) => setForm({ ...form, late_cutoff_hour: +e.target.value })} /></div>
          <div className="md:col-span-2"><Button type="submit" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save rules"}</Button></div>
        </form>
      )}

      <div>
        <h2 className="font-display text-xl">Guest flags</h2>
        <div className="mt-4 rounded-2xl border border-border bg-card/30">
          {flags.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No flags recorded.</div>
          ) : (
            <ul className="divide-y divide-border">
              {flags.map((f) => {
                const g = (f as { guests?: { full_name?: string; phone?: string } }).guests;
                return (
                  <li key={f.id} className="flex items-center justify-between gap-4 px-5 py-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">{g?.full_name ?? "Guest"} · <span className="text-xs uppercase tracking-wider text-primary">{f.category}</span></div>
                      <div className="text-xs text-muted-foreground">{f.reason}</div>
                      <div className="text-xs text-muted-foreground">{g?.phone} · {new Date(f.created_at).toLocaleDateString("en-IE")}</div>
                    </div>
                    <Button size="sm" variant={f.active ? "destructive" : "ghost"} onClick={() => toggle.mutate({ id: f.id, active: !f.active })}>
                      {f.active ? "Deactivate" : "Reactivate"}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
